import { useMemo } from 'react';

import { DatePicker, Input, Modal, Radio, RadioGroup, Select, Text } from '@thefarmersfront/kitchen';

import { FormField, ModalSection, Table } from '~/components/common';

import { useOption } from '~/hooks/stock';
import { formatTel, getStockReleaseDetailCentersColumnDefs } from '~/utils';

import { STOCK_CODES } from '~/constants';

import StockReleaseDetailSkusTable from './StockReleaseDetailSkusTable';

import { TypeStockReleaseDetailData } from '~/types/stock.types';

import styles from './StockReleaseDetailModal.module.scss';

type TypeStockReleaseDetailModalProps = {
  data: TypeStockReleaseDetailData;
  open: boolean;
  onConfirm: () => void;
};

/**
 * [재고관리 > 반출요청관리] 반출요청상세 모달
 */
const StockReleaseDetailModal = ({ data, open, onConfirm }: TypeStockReleaseDetailModalProps) => {
  const { options } = useOption({
    codes: [
      STOCK_CODES.vendorReleaseCluster,
      STOCK_CODES.vendorReleaseMethod,
      STOCK_CODES.vendorReleasePackingMethod,
      STOCK_CODES.vendorReleasePalletType,
    ],
  });

  const { getRowId: getCenterRowId, columnDefs: centersColumnDefs } = useMemo(
    () => getStockReleaseDetailCentersColumnDefs(),
    []
  );

  return (
    <Modal title="반출 요청 상세" size="xlarge" confirmText="확인" open={open} onConfirm={onConfirm}>
      <div className={styles['modal-container']}>
        <ModalSection title="반출 예정 정보">
          <FormField
            label="반출 도착지 연락처"
            contents={
              <Input
                type="text"
                size="small"
                maxWidth="289px"
                placeholder="- 포함 입력"
                disabled
                value={formatTel(data.destinationAddress?.phoneNumber) ?? ''}
              />
            }
          />
          <FormField
            label="반출 도착지 우편번호"
            contents={
              <Input
                type="text"
                size="small"
                maxWidth="289px"
                disabled
                value={data.destinationAddress?.zipCode ?? ''}
              />
            }
          />
          <FormField
            label="반출 도착지 기본주소"
            contents={
              <Input
                type="text"
                size="small"
                maxWidth="289px"
                disabled
                value={data.destinationAddress?.address ?? ''}
              />
            }
          />
          <FormField
            label="반출 도착지 상세주소"
            contents={
              <Input
                type="text"
                size="small"
                maxWidth="770px"
                placeholder="입력"
                disabled
                value={data.destinationAddress?.detailAddress ?? ''}
              />
            }
          />
          <FormField
            label="반출 방식"
            contents={
              <Select
                size="small"
                placeholder="선택"
                maxWidth="148px"
                dropdownPortalTarget={document.body}
                shouldCloseOnNotInView
                disabled
                options={options?.vendorReleaseMethod ?? []}
                value={{
                  value: data.detailInfo?.methodCodeObject.code ?? '',
                  text: data.detailInfo?.methodCodeObject.name ?? '',
                }}
              />
            }
          />
          <FormField
            label="반출 희망일"
            contents={
              <DatePicker
                size="small"
                placeholder="날짜 선택"
                maxWidth="229px"
                portalTarget={document.body}
                shouldCloseOnNotInView
                disabled
                value={new Date(data.detailInfo?.expectedDate)}
              />
            }
          />
        </ModalSection>
        <ModalSection title="출발지 정보">
          <FormField
            label="반출 출발지 센터"
            contents={
              <Select
                size="small"
                placeholder="선택"
                maxWidth="148px"
                dropdownPortalTarget={document.body}
                shouldCloseOnNotInView
                disabled
                options={options?.vendorReleaseCluster ?? []}
                value={{
                  value: data.detailInfo?.clusterCodeObject.code ?? '',
                  text: data.detailInfo?.clusterCodeObject.name ?? '',
                }}
              />
            }
          />
          <FormField
            label="반출 출발지 센터 정보"
            contentAlign={data.centerList?.length ? undefined : 'center'}
            contents={
              data.centerList?.length ? (
                <Table
                  className={styles['table']}
                  data={data.centerList}
                  columns={centersColumnDefs}
                  getRowId={getCenterRowId}
                  autoHeight
                  rowStatusType="NO_DATA"
                />
              ) : (
                <Text size="large">반출 패킹 완료 후 업데이트됩니다.</Text>
              )
            }
            contentsClassName={styles['table-container']}
          />
          {data.detailInfo?.packingMethod && (
            <FormField
              label="패킹 방식"
              contentAlign="center"
              contents={
                <RadioGroup name="packingMethod" size="medium" disabled value={data.detailInfo?.packingMethod}>
                  {(options?.vendorReleasePackingMethod ?? []).map(({ value, text }) => (
                    <Radio key={value} label={text} value={value} />
                  ))}
                </RadioGroup>
              }
            />
          )}
        </ModalSection>
        {data.palletInfo && (
          <ModalSection className={styles['pallet-info']} title="파렛트 전표 정보">
            <FormField
              label="파렛트 종류"
              contents={
                <Select
                  size="small"
                  placeholder="선택"
                  maxWidth="148px"
                  dropdownPortalTarget={document.body}
                  shouldCloseOnNotInView
                  disabled
                  options={options?.vendorReleasePalletType ?? []}
                  value={{
                    value: data.palletInfo?.palletTypeCodeObject.code ?? '',
                    text: data.palletInfo?.palletTypeCodeObject.name ?? '',
                  }}
                />
              }
            />
            <FormField
              label="하차지 물류코드"
              contents={
                <Input
                  type="text"
                  size="small"
                  maxWidth="289px"
                  placeholder="입력"
                  disabled
                  value={data.palletInfo?.logisticsCode ?? ''}
                />
              }
            />
            <FormField
              label="하차지 담당자명"
              contents={
                <Input
                  type="text"
                  size="small"
                  maxWidth="289px"
                  placeholder="입력"
                  disabled
                  value={data.palletInfo?.managerName ?? ''}
                />
              }
            />
            <FormField
              label="하차지 담당자 연락처"
              contents={
                <Input
                  type="text"
                  size="small"
                  maxWidth="289px"
                  placeholder="- 포함 입력"
                  disabled
                  value={formatTel(data.palletInfo?.managerPhoneNumber ?? '')}
                />
              }
            />
            <FormField
              label="하차지 우편번호"
              contents={
                <Input type="text" size="small" maxWidth="289px" disabled value={data.palletInfo?.zipCode ?? ''} />
              }
              contentsClassName={styles['address-container']}
            />
            <FormField
              label="하차지 기본주소"
              contents={
                <Input type="text" size="small" maxWidth="289px" disabled value={data.palletInfo?.address ?? ''} />
              }
            />
            <FormField
              label="하차지 상세주소"
              contents={
                <Input
                  type="text"
                  size="small"
                  maxWidth="770px"
                  placeholder="입력"
                  disabled
                  value={data.palletInfo?.detailAddress ?? ''}
                />
              }
            />
            <FormField
              label="하차지 담당자 이메일"
              contents={
                <Input
                  type="text"
                  size="small"
                  maxWidth="770px"
                  placeholder="입력"
                  disabled
                  value={data.palletInfo?.managerEmail ?? ''}
                />
              }
            />
          </ModalSection>
        )}
        <StockReleaseDetailSkusTable id={data.detailInfo.vendorReleaseId} />
      </div>
    </Modal>
  );
};

export default StockReleaseDetailModal;
